<script setup>
import { computed, ref, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useTodayCalculations } from '../composables/useTodayCalculations'
import { useBudgetData } from '../composables/useBudgetData'
import { useSavingsGoals } from '../composables/useSavingsGoals'
import { useRemainingBudget } from '../composables/useRemainingBudget'
import { useSavingsCalculations } from '../composables/useSavingsCalculations'
import { ConfettiParticle, createConfettiParticles } from '@/utils/confetti'

// Define emits for category switching
const emit = defineEmits(['switch-to-savings'])

// Canvas confetti system
const canvasRef = ref(null)
const confettiParticles = ref([])
let animationId = null
let isConfettiActive = false

const initConfetti = () => {
  if (!canvasRef.value) return

  const canvas = canvasRef.value
  const ctx = canvas.getContext('2d')
  if (!ctx) return

  const rect = canvas.parentElement.getBoundingClientRect()
  const devicePixelRatio = window.devicePixelRatio || 1

  // Set display size (CSS pixels)
  canvas.style.width = `${rect.width}px`
  canvas.style.height = `${rect.height}px`

  // Set actual size in memory (physical pixels)
  canvas.width = Math.round(rect.width * devicePixelRatio)
  canvas.height = Math.round(rect.height * devicePixelRatio)

  // Reset transform before scaling (CRITICAL)
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  ctx.scale(devicePixelRatio, devicePixelRatio)

  // Create confetti particles using CSS dimensions
  confettiParticles.value = createConfettiParticles(
    rect.width,
    rect.height,
    { particleCount: 30 }
  )

  isConfettiActive = true
  animateConfetti()
}

const animateConfetti = () => {
  if (!canvasRef.value || !isConfettiActive) return
  
  const canvas = canvasRef.value
  const ctx = canvas.getContext('2d')
  
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  
  // Update and draw all particles (they never get removed now)
  confettiParticles.value.forEach(particle => {
    particle.update()
    particle.draw(ctx)
  })
  
  // Continue animation indefinitely while confetti is active
  if (isConfettiActive) {
    animationId = requestAnimationFrame(animateConfetti)
  }
}

const stopConfetti = () => {
  isConfettiActive = false
  if (animationId) {
    cancelAnimationFrame(animationId)
    animationId = null
  }
  confettiParticles.value = []
  if (canvasRef.value) {
    const ctx = canvasRef.value.getContext('2d')
    ctx.clearRect(0, 0, canvasRef.value.width, canvasRef.value.height)
  }
}

const { budgetStatus, todayDebits, todayCredits, todaySavingsContributions, todayNetSpending, todayRemainingBudget, sevenDayBudgetTrend } = useTodayCalculations()
const { savingsGoal, remainingBudget, dailyRemainingBudget, dailyBudget, isViewingCurrentMonth } = useBudgetData()
const { activeGoals } = useSavingsGoals()
const {
  adjustedRemainingBudget,
  remainingSavingsTarget,
  totalDailyAllocation,
  savingsGuidanceStatus,
  formatCurrency,
  getSavingsProgress,
  getSavingsStatusText,
  getSavingsGuidanceMessage
} = useRemainingBudget()
const { savingsTrend } = useSavingsCalculations()

// Show actual savings contributions made today, not planned allocations
const actualSavingsContributions = computed(() => {
  return todaySavingsContributions.value
})

// Daily budget spending progress (includes expenses + savings - income)
const dailySpendingPercent = computed(() => {
  if (dailyBudget.value <= 0) return 0
  return Math.min((todayNetSpending.value / dailyBudget.value) * 100, 100)
})

const getRemainingTarget = () => {
  return remainingSavingsTarget.value
}

const getSavingsGuidanceClass = () => {
  return `savings-guidance-${savingsGuidanceStatus.value}`
}

const getSavingsStatusClass = () => {
  switch (savingsGuidanceStatus.value) {
    case 'target-met': return 'status-success'
    case 'on-track': return 'status-success'
    case 'partial': return 'status-warning'
    case 'no-budget': return 'status-danger'
    default: return 'status-muted'
  }
}

const getSavingsStatusTextOnly = () => {
  return getSavingsStatusText()
}

// Budget status for display
const displayStatus = computed(() => {
  const status = budgetStatus.value
  return {
    ...status,
    adjustedRemaining: adjustedRemainingBudget.value,
    showRolloverInfo: status.hasRollover && Math.abs(status.rolloverAmount) > 1 // Only show if significant
  }
})

// Trend indicator calculation
const budgetTrend = computed(() => {
  const adjusted = adjustedRemainingBudget.value
  const rollover = displayStatus.value.rolloverAmount
  
  if (adjusted > rollover) {
    return 'positive'
  } else if (adjusted < rollover) {
    return 'negative'
  } else {
    return 'neutral'
  }
})

// Show savings allocation button when there's budget available and active goals
const showSavingsAllocationButton = computed(() => {
  return adjustedRemainingBudget.value > 0 && activeGoals.value.length > 0 && remainingSavingsTarget.value > 0
})

// Handle switching to savings dashboard
const switchToSavings = () => {
  emit('switch-to-savings')
}

// Watch for target achievement to trigger confetti
watch(() => savingsGuidanceStatus.value, (newStatus, oldStatus) => {
  if (newStatus === 'target-met' && oldStatus !== 'target-met') {
    nextTick(() => {
      initConfetti()
    })
  } else if (newStatus !== 'target-met' && oldStatus === 'target-met') {
    stopConfetti()
  }
}, { immediate: false })

// Initialize confetti if already achieved on mount
onMounted(() => {
  if (savingsGuidanceStatus.value === 'target-met') {
    nextTick(() => {
      initConfetti()
    })
  }
})

// Cleanup on unmount
onUnmounted(() => {
  stopConfetti()
})
</script>

<template>
  <div class="today-remaining-card card"
    :class="{ 
      'budget-positive': adjustedRemainingBudget >= 0, 
      'budget-negative': adjustedRemainingBudget < 0,
      'past-month-view': !isViewingCurrentMonth
    }">

    <!-- Past Month Overlay -->
    <div v-if="!isViewingCurrentMonth" class="past-month-overlay">
      <div class="overlay-content">
        <i class="fa-solid fa-calendar-xmark overlay-icon"></i>
        <h4 class="overlay-title">Past Month View</h4>
        <p class="overlay-message">Today's spending guide is only available for the current month</p>
      </div>
    </div>

    <!-- Card Header -->
    <div class="card-header">
      <div class="header-icon">
        <i class="fa-solid fa-coins"></i>
      </div>
      <div class="header-content">
        <h3 class="card-title">Today's Overview</h3>
        <p class="card-subtitle">Your healthy spending guide</p>
      </div>
    </div>

    <!-- Card Body -->
    <div class="card-body">

      <div class="row mb-3">
        <div class="col">
          <div class="available-budget-section nested-container">
            <div class="highlight-header">
              <i class="fa-solid fa-dollar-sign me-1"></i>
              <span class="section-title">Healthy Spending Target</span>
            </div>
            <div class="highlight-amount">
              <span class="amount-value">{{ formatCurrency(adjustedRemainingBudget) }}</span>
              <i v-if="savingsTrend.isPositive && !savingsTrend.isNeutral" class="fa-solid fa-arrow-trend-up trend-icon trend-positive"></i>
              <i v-else-if="!savingsTrend.isPositive && !savingsTrend.isNeutral" class="fa-solid fa-arrow-trend-down trend-icon trend-negative"></i>
              <i v-else class="fa-solid fa-minus trend-icon trend-neutral"></i>
            </div>
            <div class="highlight-message">
              <span>
                <i class="fa-solid fa-check-circle me-1"></i>
                Based on your usual income and monthly essentials
              </span>
              <div class="rollover-info mt-3"
                :class="{ 
                  'rollover-positive': savingsTrend.isPositive && !savingsTrend.isNeutral, 
                  'rollover-negative': !savingsTrend.isPositive && !savingsTrend.isNeutral 
                }">
                <i class="fa-solid fa-chart-line me-1"></i>
                <span v-if="savingsTrend.isPositive && !savingsTrend.isNeutral">
                  You're ${{ Math.abs(savingsTrend.difference).toFixed(2) }} ahead of your 7-day average position
                </span>
                <span v-else-if="!savingsTrend.isPositive && !savingsTrend.isNeutral">
                  You're ${{ Math.abs(savingsTrend.difference).toFixed(2) }} behind your 7-day average position
                </span>
                <span v-else>
                  Your savings position matches your 7-day average
                </span>
              </div>
              <div class="trend-details mt-2">
                <span class="trend-detail-item">
                  <i class="fa-solid fa-piggy-bank me-1"></i>
                  Current: ${{ savingsTrend.currentPosition.toFixed(2) }}
                </span>
                <span class="trend-detail-separator">•</span>
                <span class="trend-detail-item">
                  <i class="fa-solid fa-calendar-week me-1"></i>
                  7-day avg: ${{ savingsTrend.averagePosition.toFixed(2) }}
                </span>
                <span v-if="!savingsTrend.isNeutral" class="trend-detail-separator">•</span>
                <span v-if="!savingsTrend.isNeutral" class="trend-detail-item">
                  <i class="fa-solid fa-percent me-1"></i>
                  {{ savingsTrend.isPositive ? '+' : '' }}{{ savingsTrend.percentageChange.toFixed(1) }}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Daily Spending Progress -->
      <div class="row mb-3">
        <div class="col">
          <div class="daily-spending-progress nested-container">
            <div class="highlight-header">
              <i class="fa-solid fa-receipt me-1"></i>
              <span class="section-title">Daily Budget Usage</span>
            </div>
            <div class="daily-spending-amounts">
              <span class="spent-amount">${{ todayNetSpending.toFixed(2) }}</span>
              <span class="budget-separator">of</span>
              <span class="budget-amount">${{ dailyBudget.toFixed(2) }}</span>
            </div>
            <div class="daily-spending-bar">
              <div class="daily-spending-bar-bg">
                <div 
                  class="daily-spending-bar-fill" 
                  :class="{ 'over-budget': todayNetSpending > dailyBudget }"
                  :style="{ width: dailySpendingPercent + '%' }">
                </div>
              </div>
            </div>
            <div class="daily-spending-label">
              <span v-if="todayNetSpending <= dailyBudget" class="spending-status-ok">
                <i class="fa-solid fa-circle-check me-1"></i>
                ${{ (dailyBudget - todayNetSpending).toFixed(2) }} remaining today
              </span>
              <span v-else class="spending-status-over">
                <i class="fa-solid fa-circle-exclamation me-1"></i>
                ${{ (todayNetSpending - dailyBudget).toFixed(2) }} over daily budget
              </span>
            </div>
            <div class="highlight-message mt-2">
              <span>
                <i class="fa-solid fa-circle-info me-1"></i>
                Your fixed daily target based on monthly income divided evenly across the month, including savings contributions
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Savings Goals Guidance (when active goals exist) -->
      <div v-if="activeGoals.length > 0" class="row mb-3">
        <div class="col">
          <div class="savings-guidance nested-container" :class="{ 'target-achieved': savingsGuidanceStatus === 'target-met' }">
            <!-- Canvas confetti overlay when target is met -->
            <canvas 
              v-if="savingsGuidanceStatus === 'target-met'" 
              ref="canvasRef" 
              class="confetti-canvas">
            </canvas>
            <div class="highlight-header">
              <i class="fa-solid fa-bullseye me-1"></i>
              <span class="highlight-title">Savings Goals Today</span>
            </div>
            <div class="highlight-amount">
              <span class="amount-value" v-if="savingsGuidanceStatus === 'target-met'">
                {{ totalDailyAllocation.toFixed(2) }}
              </span>
              <span class="amount-value" v-else>
                {{ getRemainingTarget().toFixed(2) }}
              </span>
            </div>
            <div class="highlight-message">
              <div class="status-section">
                <span class="status-indicator" :class="getSavingsStatusClass()">
                  <i v-if="savingsGuidanceStatus === 'target-met'" class="fa-solid fa-trophy me-1"></i>
                  {{ getSavingsStatusTextOnly() }}
                </span>
              </div>

              <!-- Progress bar when there are contributions -->
              <div v-if="actualSavingsContributions > 0 && totalDailyAllocation > 0" class="progress-section mt-3">
                <div class="progress-bar-container">
                  <div class="progress-bar-bg">
                    <div class="progress-bar-fill" :style="{ width: getSavingsProgress() + '%' }"
                      :class="{ 'progress-complete': savingsGuidanceStatus === 'target-met' }"></div>
                  </div>
                  <span class="progress-text">
                    ${{ actualSavingsContributions.toFixed(2) }} / ${{ totalDailyAllocation.toFixed(2) }}
                  </span>
                </div>
              </div>

              <div class="guidance-message mt-2">
                {{ getSavingsGuidanceMessage() }}
              </div>

              <!-- Savings Allocation Button -->
              <div v-if="showSavingsAllocationButton" class="savings-action-section mt-3">
                <button @click="switchToSavings" class="btn btn-primary btn-lg w-100 savings-allocation-btn">
                  <i class="fa-solid fa-piggy-bank me-2"></i>
                  Go to Savings
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Modern Stats Grid -->
      <div class="modern-stats-grid">
        <!-- Savings Contributions -->
        <div class="stat-card savings-card">
          <div class="stat-card-icon">
            <i class="fa-solid fa-piggy-bank"></i>
          </div>
          <div class="stat-card-content">
            <div class="stat-card-label">Today's Savings</div>
            <div class="stat-card-value">${{ actualSavingsContributions.toFixed(2) }}</div>
          </div>
        </div>

        <!-- Expenses -->
        <div class="stat-card expense-card">
          <div class="stat-card-icon">
            <i class="fa-solid fa-arrow-down"></i>
          </div>
          <div class="stat-card-content">
            <div class="stat-card-label">Today's Expenses</div>
            <div class="stat-card-value">${{ todayDebits.toFixed(2) }}</div>
          </div>
        </div>

        <!-- Income -->
        <div class="stat-card income-card">
          <div class="stat-card-icon">
            <i class="fa-solid fa-arrow-up"></i>
          </div>
          <div class="stat-card-content">
            <div class="stat-card-label">Today's Income</div>
            <div class="stat-card-value">${{ todayCredits.toFixed(2) }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Today's Remaining Budget Highlight */
.remaining-budget-highlight {
  text-align: center;
  padding: 1rem 1rem;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.highlight-header {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.75rem;
  text-transform: uppercase;
}

.highlight-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--light-text);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.highlight-amount {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.25rem;
  margin-bottom: 0.75rem;
}

.highlight-amount .currency-symbol {
  font-size: 1.75rem;
  font-weight: 600;
  color: var(--light-text-muted);
}

.highlight-amount .amount-value {
  font-size: 3.5rem;
  font-weight: 700;
  line-height: 1;
  color: var(--light-text);
}

.highlight-message {
  font-size: 0.9rem;
  font-weight: 500;
  line-height: 1.3;
  color: var(--light-text-muted);
}

/* Available Budget Section */
.available-budget-section {
  text-align: center;
  padding: 1rem 1rem;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.5rem;
}

.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--light-text);
}

.section-amount {
  display: flex;
  justify-content: center;
}

.section-amount .amount-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--light-text);
}

.today-remaining-card {
  border-radius: 5px;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  position: relative;
}

/* Past Month View Styles */
.past-month-view {
  pointer-events: none;
}

.past-month-view .card-body,
.past-month-view .card-header {
  filter: blur(4px);
  opacity: 0.4;
}

.past-month-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  background: var(--light-card);
  opacity: 0.95;
  backdrop-filter: blur(10px);
}

.overlay-content {
  text-align: center;
  padding: 2rem;
  max-width: 300px;
}

.overlay-icon {
  font-size: 3rem;
  color: var(--light-text);
  margin-bottom: 1rem;
  opacity: 1;
}

.overlay-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--light-text);
  margin-bottom: 0.5rem;
}

.overlay-message {
  font-size: 0.95rem;
  color: var(--light-text-muted);
  margin: 0;
  line-height: 1.4;
}

.header-content {
  flex: 1;
  text-align: left;
}

/* Amount Section */
.amount-section {
  margin-bottom: 1rem;
}

.amount-container {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.25rem;
  margin-bottom: 0.75rem;
}

.currency-symbol {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--light-text-muted);
}

.amount-status {
  margin-top: 0.5rem;
}



/* Statistics Section */
.stats-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 12px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
}

.stat-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  flex-shrink: 0;
}

.expense-icon {
  background: rgba(220, 53, 69, 0.1);
  color: var(--light-danger);
}

.income-icon {
  background: rgba(40, 167, 69, 0.1);
  color: var(--light-success);
}

.stat-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
}

.stat-label {
  font-size: 0.75rem;
  color: var(--light-text-muted);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1;
}

.expense-value {
  color: var(--light-danger);
}

.income-value {
  color: var(--light-success);
}

.savings-value {
  color: var(--light-primary);
}

.stat-divider {
  width: 1px;
  height: 40px;
  background: var(--light-border);
  margin: 0 1rem;
  flex-shrink: 0;
}

/* Budget Info Section */
.budget-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
  margin-top: 1rem;
}

.budget-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
}

.budget-label {
  font-size: 0.75rem;
  color: var(--light-text-muted);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.25rem;
}

.budget-value {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--light-text);
}

/* Rollover Information */
.rollover-info {
  /* Additional styling beyond nested-container */
  font-size: 0.85rem;
  font-weight: 500;
  text-align: center;
}

.rollover-positive {
  color: var(--light-success);
}

.rollover-negative {
  color: var(--light-danger);
}

/* Trend Details */
.trend-details {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: var(--light-text-muted);
  font-weight: 500;
}

.trend-detail-item {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.trend-detail-separator {
  color: var(--light-border);
  font-weight: 400;
}

/* Trend Indicator */
.trend-icon {
  font-size: 1.5rem;
  margin-left: 0.5rem;
  align-self: center;
}

.trend-positive {
  color: var(--light-success);
}

.trend-negative {
  color: var(--light-danger);
}

.trend-neutral {
  color: var(--light-text-muted);
}

/* Daily Spending Progress */
.daily-spending-progress {
  text-align: center;
  padding: 1rem;
}

.daily-spending-amounts {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.spent-amount {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--light-text);
}

.budget-separator {
  font-size: 0.9rem;
  color: var(--light-text-muted);
}

.budget-amount {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--light-text-muted);
}

.daily-spending-bar {
  margin-bottom: 0.5rem;
}

.daily-spending-bar-bg {
  height: 12px;
  background: var(--light-border);
  border-radius: 6px;
  overflow: hidden;
}

.daily-spending-bar-fill {
  height: 100%;
  background: var(--light-success);
  border-radius: 6px;
  transition: width 0.3s ease;
}

.daily-spending-bar-fill.over-budget {
  background: var(--light-danger);
}

.daily-spending-label {
  font-size: 0.85rem;
  font-weight: 500;
}

.spending-status-ok {
  color: var(--light-success);
}

.spending-status-over {
  color: var(--light-danger);
}

/* Savings Guidance */
.savings-guidance {
  text-align: center;
  padding: 1rem 1rem;
  position: relative;
  overflow: hidden;
}

.target-achieved {
  border-color: var(--light-success) !important;
}

/* Canvas Confetti */
.confetti-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.status-section {
  margin-bottom: 0.75rem;
}

.status-indicator {
  font-size: 0.9rem;
  font-weight: 500;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
}

.status-success {
  color: var(--light-text);
  background: var(--light-border);
  border: 1px solid var(--light-border);
}

.status-warning {
  color: var(--light-text);
  background: var(--light-border);
  border: 1px solid var(--light-border);
}

.status-danger {
  color: var(--light-text);
  background: var(--light-border);
  border: 1px solid var(--light-border);
}

.status-muted {
  color: var(--light-text-muted);
  background: var(--light-border);
  border: 1px solid var(--light-border);
}

.guidance-message {
  color: var(--light-text-muted);
  line-height: 1.3;
  font-size: 0.9rem;
}

/* Progress Bar */
.progress-section {
  margin-top: 0.8rem;
  margin-bottom: 0.8rem;
}

.progress-bar-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.progress-bar-bg {
  flex: 1;
  height: 20px;
  background: var(--light-border);
  border-radius: 10px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: var(--light-text);
  border-radius: 10px;
  transition: width 0.3s ease;
}

.progress-complete {
  background: var(--light-success) !important;
}

.progress-text {
  font-size: 0.75rem;
  color: var(--light-text-muted);
  white-space: nowrap;
}

/* Modern Stats Grid */
.modern-stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.stat-card {
  background: var(--nested-container-bg);
  border-radius: 8px;
  padding: 1rem;
  transition: all 0.2s ease;
  border: 2px dashed var(--light-border);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.75rem;
}

.stat-card-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  transition: all 0.2s ease;
}

.savings-card .stat-card-icon {
  background: rgba(54, 162, 235, 0.1);
  color: #36A2EB;
}

.expense-card .stat-card-icon {
  background: rgba(220, 53, 69, 0.1);
  color: var(--light-danger);
}

.income-card .stat-card-icon {
  background: rgba(40, 167, 69, 0.1);
  color: var(--light-success);
}

.stat-card-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  width: 100%;
}

.stat-card-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--light-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-card-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--light-text);
}

.stat-card:hover .stat-card-icon {
  transform: scale(1.1);
}

/* Savings Allocation Button */
.savings-action-section {
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--light-border);
}

.savings-allocation-btn {
  background-color: #36A2EB;
  border-color: #36A2EB;
  min-height: 50px;
  font-weight: 600;
  transition: all 0.2s ease;
}

.savings-allocation-btn:hover {
  background-color: #2e8bc7;
  border-color: #2e8bc7;
}

/* Dark Mode Adjustments */
:global([data-theme]) .stats-section {
  background: rgba(255, 255, 255, 0.05);
}

:global([data-theme]) .budget-info {
  background: rgba(255, 255, 255, 0.05);
}

:global([data-theme]) .target-achieved {
  border-color: var(--light-success) !important;
}

:global([data-theme]) .stat-card:hover {
  box-shadow: 0 4px 12px rgba(255, 255, 255, 0.1);
}

:global([data-theme]) .savings-card .stat-card-icon {
  background: rgba(54, 162, 235, 0.2);
}

:global([data-theme]) .expense-card .stat-card-icon {
  background: rgba(255, 107, 107, 0.2);
}

:global([data-theme]) .income-card .stat-card-icon {
  background: rgba(81, 207, 102, 0.2);
}

/* Responsive Design */
@media (max-width: 768px) {
  .highlight-amount .currency-symbol {
    font-size: 1.5rem;
  }

  .highlight-amount .amount-value {
    font-size: 2.5rem;
  }

  .remaining-budget-highlight {
    padding: 1rem 0.75rem;
  }

  .currency-symbol {
    font-size: 1.25rem;
  }

  /* Modern Stats Grid - Stack on Mobile */
  .modern-stats-grid {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .stat-card {
    flex-direction: row;
    text-align: left;
    padding: 0.75rem;
  }

  .stat-card-icon {
    width: 40px;
    height: 40px;
    font-size: 1.25rem;
    flex-shrink: 0;
  }

  .stat-card-content {
    align-items: flex-start;
  }

  .stat-card-label {
    font-size: 0.75rem;
  }

  .stat-card-value {
    font-size: 1.25rem;
  }

  .stats-section {
    flex-direction: column;
    gap: 1rem;
    padding: 0.75rem;
  }

  .stat-item {
    width: 100%;
    justify-content: center;
  }

  .stat-divider {
    width: 100%;
    height: 1px;
    margin: 0;
  }

  .stat-content {
    align-items: center;
    text-align: center;
  }

  .budget-info {
    flex-direction: column;
    gap: 0.75rem;
  }

  .budget-item {
    width: 100%;
  }

  /* Hide card completely on mobile when viewing past months */
  .past-month-view {
    display: none;
  }
}
</style>